import { useState } from "react";
import { useChatContext } from "stream-chat-react";
import type { Channel } from "stream-chat";
import { Resources, useResource } from "Translations/Resources";
import { toast } from "sonner";

const RESOURCES = Resources.Chat.MultiPayerRequest;

/**
 * Closing a multi-payer request is the author editing their own message: the attachment's
 * `isClosed` flag is flipped, nothing happens server-side (ADR-0009).
 */
export const useCloseMultiPayerRequest = (
  channel: Channel,
  messageId: string,
) => {
  const [isClosing, setIsClosing] = useState(false);
  const { client } = useChatContext();
  const { t } = useResource();

  const close = async () => {
    // The message in the render context has its caption blanked; the update must carry the
    // original text or the push/preview caption is lost with it.
    const original = channel.state.findMessage(messageId);

    if (!original) {
      return;
    }

    setIsClosing(true);

    try {
      await client.updateMessage({
        id: messageId,
        text: original.text,
        attachments: (original.attachments ?? []).map((a) =>
          a.type === "payment_request_multi" ? { ...a, isClosed: true } : a,
        ),
      });
    } catch {
      toast.error(t(RESOURCES.CloseError));
    } finally {
      setIsClosing(false);
    }
  };

  return { close, isClosing };
};
